import { useTranslation } from 'react-i18next';
import type { MonthVerdict } from '@/application/metrics';
import { resolveVerdictPhraseKey } from '@/application/metrics';
import { formatSigned } from '@/presentation/utils/metricDeltas';

const STATUS_STYLES: Record<
  MonthVerdict['status'],
  { shell: string; badge: string }
> = {
  good: {
    shell: 'border-teal-200/80 bg-teal-50/90 dark:border-teal-900/50 dark:bg-teal-950/30',
    badge: 'bg-teal-600 text-white dark:bg-teal-500',
  },
  watch: {
    shell: 'border-amber-200/80 bg-amber-50/90 dark:border-amber-900/50 dark:bg-amber-950/30',
    badge: 'bg-amber-600 text-white dark:bg-amber-500',
  },
  critical: {
    shell: 'border-red-200/80 bg-red-50/90 dark:border-red-900/50 dark:bg-red-950/35',
    badge: 'bg-red-600 text-white dark:bg-red-500',
  },
};

interface MonthVerdictBannerProps {
  verdict: MonthVerdict;
  locale: string;
  /** Frases sin copy de “N clínicas” / ranking de red. */
  variant?: 'multi' | 'mono';
}

/** Franja superior: estado del mes + una frase del porqué. */
export function MonthVerdictBanner({ verdict, locale, variant = 'multi' }: MonthVerdictBannerProps) {
  const { t } = useTranslation();
  const styles = STATUS_STYLES[verdict.status];
  const phraseKey = resolveVerdictPhraseKey(verdict.reasons);
  const delta =
    verdict.revenueDeltaPct === null ? null : formatSigned(locale, verdict.revenueDeltaPct);
  const phrasesRoot =
    variant === 'mono' ? 'dashboard.monthVerdict.phrasesMono' : 'dashboard.monthVerdict.phrases';

  return (
    <section
      aria-label={t('dashboard.monthVerdict.ariaLabel')}
      className={`metric-hero-banner ${styles.shell}`}
    >
      <div className="flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center">
        <span
          className={`inline-flex w-fit items-center rounded-md px-2.5 py-1 text-xs font-bold uppercase tracking-[0.08em] ${styles.badge}`}
        >
          {t(`dashboard.monthVerdict.status.${verdict.status}`)}
        </span>
        <p className="metric-hero-banner__phrase">
          {t(`${phrasesRoot}.${phraseKey}`, {
            delta: delta ?? '—',
            alertClinics: verdict.alertClinics,
            total: verdict.totalClinics,
            issues: verdict.criticalInsights,
          })}
        </p>
      </div>
    </section>
  );
}
